import { PropTypeMediaEntry } from "fleet/prop-types";
import PropTypes from "prop-types";
import React from "react";
import Col from "react-bootstrap/Col";
import FormLabel from "react-bootstrap/FormLabel";
import FormControl from "react-bootstrap/FormControl";
import FormGroup from "react-bootstrap/FormGroup";
import Row from "react-bootstrap/Row";
import { Creatable } from "react-select";
import { Notification } from "ui/notification";
import { configReducer } from "util/array";
import { stopPropagation } from "util/components";
import { arrayToSelectOptions } from "util/select";
import { utcToLocal } from "util/time";

class EditContentForm extends React.PureComponent {
  static get propTypes() {
    return {
      excludeFields: PropTypes.arrayOf(
        PropTypes.oneOf([
          "category",
          "conflictsWith",
          "duration",
          "name",
          "uploadDate",
          "url",
        ]),
      ),
      categories: PropTypes.arrayOf(PropTypes.string),
      getState: PropTypes.func,
      mediaEntry: PropTypeMediaEntry,
      readOnlyFields: PropTypes.arrayOf(
        PropTypes.oneOf([
          "all",
          "category",
          "conflictsWith",
          "duration",
          "name",
          "url",
        ]),
      ),
    };
  }

  static get defaultProps() {
    return {
      categories: [],
      excludeFields: [],
      getState: () => {},
      mediaEntry: {},
      readOnlyFields: [],
    };
  }

  constructor(props) {
    super(props);

    this.state = this._getFormState(props.mediaEntry);

    this._onCategoryChange = this._onCategoryChange.bind(this);
    this._onConflictsWithChange = this._onConflictsWithChange.bind(this);
    this._onDurationChange = this._onChange.bind(this, "duration");
    this._onNameChange = this._onChange.bind(this, "name");
    this._onUrlChange = this._onChange.bind(this, "url");
    this.exportState = this.exportState.bind(this);

    props.getState(this.constructor.name, this.exportState);
  }

  componentDidUpdate(prevProps) {
    const { mediaEntry } = this.props;
    if (mediaEntry !== prevProps.mediaEntry) {
      this.setState(this._getFormState(mediaEntry));
    }
  }

  _getOptions(values) {
    return Array.isArray(values) ? arrayToSelectOptions(values) : null;
  }

  _getFormState(mediaEntry) {
    const {
      conflictsWith,
      duration: durationMs,
      id,
      name,
      tag,
      type,
      uploadDate,
      url,
    } = mediaEntry;
    const duration = durationMs / 1000;

    return {
      duration,
      id,
      name,
      tag,
      type,
      uploadDate,
      url,
      conflictsWith: this._getOptions(conflictsWith),
    };
  }

  _onChange(propName, e) {
    const { value } = e.target;

    this.setState({ [propName]: value });
  }

  _onCategoryChange(e) {
    if (e === null) {
      this.setState({ tag: e });
    } else {
      this.setState({ tag: e.value });
    }
  }

  _onConflictsWithChange(e) {
    if (e === null) {
      this.setState({ conflictsWith: [] });
    } else {
      this.setState({ conflictsWith: e });
    }
  }

  exportState() {
    const { conflictsWith, duration: durationSecs } = this.state;
    return {
      ...this.state,
      conflictsWith: conflictsWith ? conflictsWith.map((cw) => cw.value) : null,
      duration: durationSecs * 1000,
    };
  }

  render() {
    const { categories, excludeFields, readOnlyFields } = this.props;
    const { conflictsWith, duration, name, tag, uploadDate, url } = this.state;
    const exclude = excludeFields.reduce(configReducer, {});
    const readOnly = readOnlyFields.reduce(configReducer, {});
    const categoryOptions = arrayToSelectOptions(categories);

    const readOnlyForm = readOnly.all;

    return (
      <div className="edit-user-content-form">
        <Row>
          <Col xs={12}>
            <Notification />
          </Col>
        </Row>
        {!exclude.name && (
          <Row>
            <Col xs={12}>
              <FormGroup>
                <FormLabel>Name</FormLabel>
                <FormControl
                  key="content-form-name"
                  onChange={this._onNameChange}
                  onKeyDown={stopPropagation}
                  placeholder="Name"
                  readOnly={Boolean(readOnlyForm || readOnly.name)}
                  required={true}
                  type="text"
                  value={name}
                />
              </FormGroup>
            </Col>
          </Row>
        )}
        {!exclude.uploadDate && (
          <Row>
            <Col xs={12}>
              <FormGroup>
                <FormLabel>Upload Date</FormLabel>
                <FormControl
                  key="content-form-uploadDate"
                  onKeyDown={stopPropagation}
                  readOnly={true}
                  type="text"
                  value={utcToLocal(uploadDate)}
                />
              </FormGroup>
            </Col>
          </Row>
        )}
        {!exclude.url && (
          <Row>
            <Col xs={12}>
              <FormGroup>
                <FormLabel>URL</FormLabel>
                <FormControl
                  key="content-form-url"
                  onChange={this._onUrlChange}
                  onKeyDown={stopPropagation}
                  placeholder="https://www.vistarmedia.com"
                  readOnly={Boolean(readOnlyForm || readOnly.url)}
                  required={true}
                  type="url"
                  value={url}
                />
              </FormGroup>
            </Col>
          </Row>
        )}
        {!exclude.duration && (
          <Row>
            <Col xs={12}>
              <FormGroup>
                <FormLabel>Duration (in seconds)</FormLabel>
                <FormControl
                  key="content-form-duration"
                  min="0"
                  onChange={this._onDurationChange}
                  onKeyDown={stopPropagation}
                  placeholder="Duration (seconds)"
                  readOnly={Boolean(readOnlyForm || readOnly.duration)}
                  required={true}
                  type="number"
                  value={duration}
                />
              </FormGroup>
            </Col>
          </Row>
        )}
        {!exclude.category && (
          <Row>
            <Col xs={12}>
              <FormGroup>
                <FormLabel>Creative Category</FormLabel>
                <Creatable
                  key="content-form-category"
                  isDisabled={Boolean(readOnlyForm || readOnly.category)}
                  name="category"
                  onChange={this._onCategoryChange}
                  options={categoryOptions}
                  placeholder="Creative Category"
                  value={tag ? { label: tag, value: tag } : null}
                />
              </FormGroup>
            </Col>
          </Row>
        )}
        {!exclude.conflictsWith && (
          <Row>
            <Col xs={12}>
              <FormGroup>
                <FormLabel>Creative Category Restrictions</FormLabel>
                <Creatable
                  key="content-form-conflictsWith"
                  isDisabled={Boolean(readOnlyForm || readOnly.conflictsWith)}
                  name="conflictsWith"
                  onChange={this._onConflictsWithChange}
                  options={categoryOptions}
                  placeholder="Creative Category Restrictions"
                  value={conflictsWith}
                  isMulti
                />
              </FormGroup>
            </Col>
          </Row>
        )}
      </div>
    );
  }
}

export { EditContentForm };
